/**
 * Pure geometry helpers behind LTD lateral-length trimming in the Unit Builder.
 *
 * Ported from the mock (`SciChartGunBarrel.tsx` / `EditSlotMap.tsx`): a lateral
 * is edited purely as its two endpoint coordinates, and its length is always
 * DERIVED — never stored. Trims are parameterised along the FULL-length axis so
 * neither end is a moving origin: `trimFrom: 'high'` holds the low end and pulls
 * the high end in; `'low'` holds the high end. The full-length reference is the
 * API geometry before any overlay edit; the editors may also extend out to
 * {@link extendLateralToBoundary}, the unit boundary less the in-line setback.
 *
 * Coordinates are `[lng, lat]` GeoJSON positions, as the Mapbox map uses.
 */

/** Shortest a lateral can be trimmed to, in feet — matches the mock's floor. */
export const MIN_LTD_LEN_FT = 500;

/** Which end a length edit is taken off. Low = first coord, high = last coord. */
export type TrimFrom = 'low' | 'high';

const FT_PER_METER = 3.28084;
const M_PER_DEG_LAT = 111_320;

/**
 * Planar feet between two `[lng, lat]` points, via a local equirectangular
 * approximation referenced at their mean latitude — accurate to well under a
 * foot over lateral-length distances, and matching the mock's planar measure.
 *
 * @param {GeoJSON.Position} a - First point `[lng, lat]`.
 * @param {GeoJSON.Position} b - Second point `[lng, lat]`.
 * @return {number} Distance in feet.
 */
const segmentFt = (a: GeoJSON.Position, b: GeoJSON.Position): number => {
  const midLatRad = (((a[1] + b[1]) / 2) * Math.PI) / 180;
  const dxM = (b[0] - a[0]) * M_PER_DEG_LAT * Math.cos(midLatRad);
  const dyM = (b[1] - a[1]) * M_PER_DEG_LAT;
  return Math.hypot(dxM, dyM) * FT_PER_METER;
};

/**
 * Total length of a lateral polyline, in feet.
 *
 * @param {GeoJSON.Position[]} coords - Lateral coordinates.
 * @return {number} Length in feet (0 for a degenerate line).
 */
export const lateralLengthFt = (coords: GeoJSON.Position[]): number => {
  let total = 0;
  for (let i = 1; i < coords.length; i += 1) {
    total += segmentFt(coords[i - 1], coords[i]);
  }
  return total;
};

// True when `moved` lies beyond `end`, away from `other` (the lateral's far end).
const isOutward = (
  end: GeoJSON.Position,
  moved: GeoJSON.Position,
  other: GeoJSON.Position
): boolean => {
  const k = Math.cos((end[1] * Math.PI) / 180);
  const dot =
    (moved[0] - end[0]) * (other[0] - end[0]) * k * k +
    (moved[1] - end[1]) * (other[1] - end[1]);
  return dot < 0;
};

/**
 * The per-end trim amounts (ft) between a lateral's current server geometry and
 * a trimmed overlay — how far the heel (first coord) and toe (last coord) each
 * move IN. Maps the front-end trim overlay to the Slot Editing API's
 * `trimStartFt` (heel) / `trimEndFt` (toe). An end moved OUT past the server
 * line comes back negative, which the API reads as an extension.
 *
 * @param {GeoJSON.Position[]} serverCoords - The slot's current (un-overlaid) lateral.
 * @param {GeoJSON.Position[]} trimmedCoords - The trimmed overlay coordinates.
 * @return {object} `{ trimStartFt, trimEndFt }` heel and toe cut amounts, in feet.
 */
export const lateralTrimsFt = (
  serverCoords: GeoJSON.Position[],
  trimmedCoords: GeoJSON.Position[]
): { trimStartFt: number; trimEndFt: number } => {
  if (serverCoords.length < 2 || trimmedCoords.length < 2) {
    return { trimStartFt: 0, trimEndFt: 0 };
  }
  const serverStart = serverCoords[0];
  const serverEnd = serverCoords[serverCoords.length - 1];
  const start = trimmedCoords[0];
  const end = trimmedCoords[trimmedCoords.length - 1];
  const trimStartFt = segmentFt(serverStart, start);
  const trimEndFt = segmentFt(end, serverEnd);
  return {
    trimStartFt: isOutward(serverStart, start, serverEnd)
      ? -trimStartFt
      : trimStartFt,
    trimEndFt: isOutward(serverEnd, end, serverStart) ? -trimEndFt : trimEndFt,
  };
};

/** Kept clear of the boundary on top of the setback, for planar rounding. */
const EXTEND_MARGIN_FT = 10;

/**
 * The longest a lateral may be edited out to: its line carried on in both
 * directions to the unit boundary, less the in-line setback at each end (where
 * generation places the ends), and never shorter than the lateral itself. The
 * lateral's own coords come back unchanged whenever that can't be worked out
 * (no ring or setback yet, not a two-point stick, no boundary crossing beyond
 * an end), so an editor using it behaves exactly as before.
 *
 * @param {GeoJSON.Position[]} coords - The server lateral, low end first.
 * @param {GeoJSON.Position[] | null} ring - The unit's outer ring, `[lng, lat]`.
 * @param {number | null} inLineSetbackFt - The zone's in-line setback, ft.
 * @return {GeoJSON.Position[]} The extended stick, same direction as `coords`.
 */
export const extendLateralToBoundary = (
  coords: GeoJSON.Position[],
  ring: GeoJSON.Position[] | null,
  inLineSetbackFt: number | null
): GeoJSON.Position[] => {
  if (
    coords.length !== 2 ||
    !ring ||
    ring.length < 3 ||
    inLineSetbackFt === null ||
    !Number.isFinite(inLineSetbackFt) ||
    inLineSetbackFt < 0
  ) {
    return coords;
  }
  const [start, end] = coords;
  const ftPerLng =
    M_PER_DEG_LAT * Math.cos((start[1] * Math.PI) / 180) * FT_PER_METER;
  const ftPerLat = M_PER_DEG_LAT * FT_PER_METER;
  const toXy = (p: GeoJSON.Position): [number, number] => [
    (p[0] - start[0]) * ftPerLng,
    (p[1] - start[1]) * ftPerLat,
  ];
  const [ex, ey] = toXy(end);
  const len = Math.hypot(ex, ey);
  if (!(len > 0)) return coords;
  const ux = ex / len;
  const uy = ey / len;

  // The nearest boundary crossings before the start and past the end, as
  // distances along the line from the start.
  let lo = -Infinity;
  let hi = Infinity;
  for (let i = 0; i < ring.length; i += 1) {
    const [ax, ay] = toXy(ring[i]);
    const [bx, by] = toXy(ring[(i + 1) % ring.length]);
    const dx = bx - ax;
    const dy = by - ay;
    const denom = ux * dy - uy * dx;
    if (Math.abs(denom) < 1e-9) continue;
    const s = (ax * uy - ay * ux) / denom;
    if (s < 0 || s > 1) continue;
    const t = (ax * dy - ay * dx) / denom;
    if (t <= 0.5) lo = Math.max(lo, t);
    if (t >= len - 0.5) hi = Math.min(hi, t);
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return coords;

  // Less than half a foot of room is no room: that end stays exactly as is.
  const inset = inLineSetbackFt + EXTEND_MARGIN_FT;
  const from = lo + inset < -0.5 ? lo + inset : 0;
  const to = hi - inset > len + 0.5 ? hi - inset : len;
  const at = (d: number): GeoJSON.Position => {
    const p: GeoJSON.Position = [
      start[0] + (ux * d) / ftPerLng,
      start[1] + (uy * d) / ftPerLat,
    ];
    return start.length > 2 ? [...p, start[2]] : p;
  };
  return [from === 0 ? start : at(from), to === len ? end : at(to)];
};

// Linear interpolation of a `[lng, lat]` point a fraction `t` along a segment.
const lerp = (
  a: GeoJSON.Position,
  b: GeoJSON.Position,
  t: number
): GeoJSON.Position => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

/**
 * The leading `keepFt` of a polyline, measured from its FIRST coord — the tail
 * beyond `keepFt` is dropped. Used when trimming from the high end (low held).
 *
 * @param {GeoJSON.Position[]} coords - Full lateral coordinates.
 * @param {number} keepFt - Length to keep from the start, in feet.
 * @return {GeoJSON.Position[]} The truncated polyline (>= 2 coords).
 */
const truncateFromStart = (
  coords: GeoJSON.Position[],
  keepFt: number
): GeoJSON.Position[] => {
  const out: GeoJSON.Position[] = [coords[0]];
  let remaining = keepFt;
  for (let i = 1; i < coords.length; i += 1) {
    const seg = segmentFt(coords[i - 1], coords[i]);
    if (seg <= 0) continue;
    if (remaining >= seg) {
      out.push(coords[i]);
      remaining -= seg;
    } else {
      out.push(lerp(coords[i - 1], coords[i], remaining / seg));
      return out;
    }
  }
  return out.length >= 2 ? out : [coords[0], coords[coords.length - 1]];
};

/**
 * The trailing `keepFt` of a polyline, ending at its LAST coord — the head
 * before that is dropped. Used when trimming from the low end (high held).
 *
 * @param {GeoJSON.Position[]} coords - Full lateral coordinates.
 * @param {number} keepFt - Length to keep from the end, in feet.
 * @return {GeoJSON.Position[]} The truncated polyline (>= 2 coords).
 */
const truncateFromEnd = (
  coords: GeoJSON.Position[],
  keepFt: number
): GeoJSON.Position[] => {
  const reversed = [...coords].reverse();
  return truncateFromStart(reversed, keepFt).reverse();
};

// Clamp a target length to `[MIN_LTD_LEN_FT, fullLen]` (floor never above full).
const clampLength = (targetFt: number, fullFt: number): number =>
  Math.max(Math.min(MIN_LTD_LEN_FT, fullFt), Math.min(fullFt, targetFt));

/**
 * Trim (or restore) a lateral to a target length off the chosen end, holding the
 * other end fixed. Clamped to the 500 ft floor and the full generated length.
 *
 * @param {GeoJSON.Position[]} fullCoords - The full (un-trimmed) lateral.
 * @param {number} targetFt - Desired length, in feet.
 * @param {TrimFrom} trimFrom - Which end moves in.
 * @return {GeoJSON.Position[]} The trimmed coordinates.
 */
export const trimLateralToLength = (
  fullCoords: GeoJSON.Position[],
  targetFt: number,
  trimFrom: TrimFrom
): GeoJSON.Position[] => {
  if (fullCoords.length < 2) return fullCoords;
  const len = clampLength(targetFt, lateralLengthFt(fullCoords));
  return trimFrom === 'high'
    ? truncateFromStart(fullCoords, len)
    : truncateFromEnd(fullCoords, len);
};

/**
 * The portion of a lateral between two lengths-from-start (`fromFt`..`toFt`),
 * both measured along the FULL line from its first coord. Unlike
 * {@link trimLateralToLength} (which anchors one end at the full geometry), this
 * holds BOTH ends independently — so a map grip drag can move only the grabbed
 * end and leave the other exactly where it is (no snap back to the full end / the
 * unit edge).
 *
 * @param {GeoJSON.Position[]} coords - The full (un-trimmed) lateral.
 * @param {number} fromFt - Start distance from the first coord, in feet.
 * @param {number} toFt - End distance from the first coord, in feet.
 * @return {GeoJSON.Position[]} The sub-lateral (>= 2 coords).
 */
export const sliceLateralByLength = (
  coords: GeoJSON.Position[],
  fromFt: number,
  toFt: number
): GeoJSON.Position[] => {
  if (coords.length < 2) return coords;
  const lo = Math.max(0, Math.min(fromFt, toFt));
  const hi = Math.max(fromFt, toFt);
  // Keep the leading `hi` feet, then keep the trailing `hi - lo` of that — i.e.
  // the segment [lo, hi]. Reuses the same truncation the trim helpers use.
  return truncateFromEnd(truncateFromStart(coords, hi), hi - lo);
};

/**
 * Set a lateral to a target length by moving ONE end, holding the OTHER end at
 * its CURRENT position — the rail's "Lateral length" field / nudge with the
 * Trim-from switch. Unlike {@link trimLateralToLength} (which anchors the held
 * end at the full geometry, snapping a previously-trimmed end back to the unit
 * edge), the held end stays exactly where it is now.
 *
 * `trimFrom: 'high'` moves the high end (holds the low end); `'low'` moves the
 * low end (holds the high end). Clamped to the 500 ft floor and to what fits
 * between the held end and the full lateral's end.
 *
 * @param {GeoJSON.Position[]} full - The full (un-trimmed) lateral.
 * @param {GeoJSON.Position[]} current - The current (possibly-trimmed) lateral.
 * @param {number} targetFt - Desired length, in feet.
 * @param {TrimFrom} trimFrom - Which end moves.
 * @return {GeoJSON.Position[]} The resized coordinates.
 */
export const resizeLateralToLength = (
  full: GeoJSON.Position[],
  current: GeoJSON.Position[],
  targetFt: number,
  trimFrom: TrimFrom
): GeoJSON.Position[] => {
  if (full.length < 2 || current.length < 2) return current;
  const fullLen = lateralLengthFt(full);
  const lowFt = projectCursorToLengthFromStart(full, current[0]);
  const highFt = projectCursorToLengthFromStart(
    full,
    current[current.length - 1]
  );
  if (trimFrom === 'high') {
    // Hold the low end; the high end sets the length (capped by the full end).
    const len = Math.min(fullLen - lowFt, Math.max(MIN_LTD_LEN_FT, targetFt));
    return sliceLateralByLength(full, lowFt, lowFt + len);
  }
  // Hold the high end; the low end sets the length (capped by the full start).
  const len = Math.min(highFt, Math.max(MIN_LTD_LEN_FT, targetFt));
  return sliceLateralByLength(full, highFt - len, highFt);
};

/**
 * Cumulative length (ft) from the FIRST coord to the point on the polyline
 * nearest the cursor — the drag's read-out along the full-length axis. Feeds
 * {@link trimLateralToLength} as the target for a high-end drag, or `fullLen −
 * this` for a low-end drag.
 *
 * @param {GeoJSON.Position[]} fullCoords - The full lateral.
 * @param {GeoJSON.Position} cursor - Cursor position `[lng, lat]`.
 * @return {number} Distance from the start, in feet, clamped to the length.
 */
export const projectCursorToLengthFromStart = (
  fullCoords: GeoJSON.Position[],
  cursor: GeoJSON.Position
): number => {
  const mLng = M_PER_DEG_LAT * Math.cos((cursor[1] * Math.PI) / 180);
  // Work in a local planar frame (metres) so the projection is a plain 2D one.
  const toXy = (p: GeoJSON.Position): [number, number] => [
    (p[0] - fullCoords[0][0]) * mLng,
    (p[1] - fullCoords[0][1]) * M_PER_DEG_LAT,
  ];
  const c = toXy(cursor);
  let best = { dist2: Infinity, lenFt: 0 };
  let acc = 0;
  for (let i = 1; i < fullCoords.length; i += 1) {
    const a = toXy(fullCoords[i - 1]);
    const b = toXy(fullCoords[i]);
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const segLen2 = abx * abx + aby * aby;
    const t =
      segLen2 > 0
        ? Math.max(
            0,
            Math.min(1, ((c[0] - a[0]) * abx + (c[1] - a[1]) * aby) / segLen2)
          )
        : 0;
    const px = a[0] + abx * t;
    const py = a[1] + aby * t;
    const dist2 = (c[0] - px) ** 2 + (c[1] - py) ** 2;
    if (dist2 < best.dist2) {
      const segFt = segmentFt(fullCoords[i - 1], fullCoords[i]);
      best = { dist2, lenFt: acc + segFt * t };
    }
    acc += segmentFt(fullCoords[i - 1], fullCoords[i]);
  }
  return best.lenFt;
};

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

// 8-point compass label for a bearing in degrees (0 = north, clockwise).
const compass = (bearingDeg: number): string => {
  const idx = Math.round(((bearingDeg % 360) + 360) / 45) % 8;
  return COMPASS[idx];
};

/**
 * Compass labels for a lateral's two ends, from the low→high bearing — so the
 * rail's Trim-from switch and the map grips name the same physical end (e.g. a
 * north–south lateral reads low = "S", high = "N").
 *
 * @param {GeoJSON.Position[]} coords - Lateral coordinates.
 * @return {{ lowLabel: string, highLabel: string }} End labels.
 */
export const nameLateralEnds = (
  coords: GeoJSON.Position[]
): { lowLabel: string; highLabel: string } => {
  if (coords.length < 2) return { lowLabel: 'Low', highLabel: 'High' };
  const low = coords[0];
  const high = coords[coords.length - 1];
  const midLatRad = (((low[1] + high[1]) / 2) * Math.PI) / 180;
  const dx = (high[0] - low[0]) * Math.cos(midLatRad);
  const dy = high[1] - low[1];
  const bearing = (Math.atan2(dx, dy) * 180) / Math.PI;
  return { lowLabel: compass(bearing + 180), highLabel: compass(bearing) };
};
